import React, { useEffect, useMemo, useRef } from 'react'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { useUpdateAccountMutation } from '../../../../store/accountUserSettings/accountUserSettings'
import { useAccount } from '../../../../common/useAccount'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { FormFeedback } from '../../../../components/Form/FormFeedback'
import { Button } from '../../../../components/Button/Button'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { Account } from '../../../../models/Account'
import { useZocksQuota } from '../../../../components/ScopedBlock/hooks'
import { useGetAllUsersQuery } from '../../../../store/user/user'
import { sanitizeAccountConfig } from './sanitizeAccountConfig'
import { AccountTimezoneSettings } from './AccountTimezoneSettings'
import { AccountRegionalSettings } from './AccountRegionalSettings'
import { EnterpriseManagedBanner } from '../EnterpriseManagedBanner'
import { AccountResidencySettings } from './AccountResidencySettings/AccountResidencySettings'
import { BusinessAddressSettings } from './BusinessAddressSettings/BusinessAddressSettings'
import { ClientQueryUserAccessTable } from './ClientQueryUserAccessTable'

interface FormValues {
  accountName: string
  crossAccountAccess: boolean
  inviteLinkAutoRenewalEnabled: boolean
  crossClientQueryAiPhaseAllowed: boolean
}

const DEFAULT_FORM_VALUES: FormValues = {
  accountName: '',
  crossAccountAccess: false,
  inviteLinkAutoRenewalEnabled: true,
  crossClientQueryAiPhaseAllowed: false,
}

const getFormValuesFromAccount = (account: Account): FormValues => ({
  accountName: account.name,
  crossAccountAccess: account.effectiveConfig.crossAccountAccess,
  inviteLinkAutoRenewalEnabled: account.effectiveConfig.inviteLinkAutoRenewal,
  crossClientQueryAiPhaseAllowed: account.effectiveConfig.crossClientQueryAiPhaseAllowed,
})

export const AccountSettings: React.FC = () => {
  const { accountId, account, isFetching: accountLoading } = useAccount({ refetchOnMountOrArgChange: true })
  const [updateAccount, { isLoading: accountUpdateLoading }] = useUpdateAccountMutation()
  const { hasQuota } = useZocksQuota()
  const { data: usersData, isFetching: isUsersLoading } = useGetAllUsersQuery(
    { accountId, identityTypes: ['idp'] },
    { refetchOnMountOrArgChange: true, skip: !accountId },
  )
  const validUserIds = useMemo(() => (usersData && !isUsersLoading ? new Set(usersData.data.map((user) => user.id)) : null), [usersData, isUsersLoading])

  const crossAccountLoginQuota = hasQuota('auth.crossAccountLoginEnabled.value')
  const inviteLinkAutoRenewalQuota = hasQuota('auth.inviteLinkAutoRenewal.value')
  const cqQuota = hasQuota('ai.crossClientQuery.value')
  const crossAccountAccessIsManagedAtTheEnterpriseLevel = account?.enterpriseConfig.crossAccountAccess != null
  const accountNameIsLockedAtEnterpriseLevel = account?.enterpriseConfig.lockAccountName === true
  const inviteLinkAutoRenewalIsManagedAtEnterpriseLevel = account?.enterpriseConfig.inviteLinkAutoRenewal != null
  const crossClientQueryAiPhaseAllowedIsManagedAtTheEnterpriseLevel = account?.enterpriseConfig.crossClientQueryAiPhaseAllowed != null
  // Role allowlist overrides the user-id allowlist, so editing the latter here would be silently pointless.
  const crossClientQueryAiPhaseAllowedRolesIsManagedAtTheEnterpriseLevel = account?.enterpriseConfig.crossClientQueryAiPhaseAllowedRoles != null

  const {
    control,
    handleSubmit,
    formState: { dirtyFields },
    resetField,
    reset,
  } = useForm<FormValues>({ defaultValues: DEFAULT_FORM_VALUES })

  // Account arrives asynchronously from the shared query cache, so the form's initial defaultValues
  // can't depend on it. Sync once it first loads; later cache refreshes (from this page's own saves,
  // including sibling widgets like ClientQueryUserAccessTable) are handled by each submit handler
  // resetting only the fields it owns, so this doesn't clobber an in-progress edit elsewhere.
  // Waits for accountLoading to clear because useAccount's refetchOnMountOrArgChange can hand back a
  // cached account while a fresher one is still in flight; locking the guard on that stale value would
  // make the effect ignore the real response once it lands.
  const hasInitializedForm = useRef(false)
  useEffect(() => {
    if (!account || accountLoading || hasInitializedForm.current) {
      return
    }
    hasInitializedForm.current = true
    reset(getFormValuesFromAccount(account))
  }, [account, accountLoading, reset])

  const isAccountNameDirty = dirtyFields.accountName
  const crossClientQueryAiPhaseAllowed = useWatch({ control, name: 'crossClientQueryAiPhaseAllowed' })

  const onCancelAccountNameChange = () => {
    resetField('accountName')
  }

  const onSubmitAccountNameChange = async (data: FormValues) => {
    const result = await updateAccount({
      accountId,
      payload: {
        name: data.accountName,
      },
    })
    if ('data' in result && result.data) {
      reset(getFormValuesFromAccount(result.data.data))
    }
  }

  const onSubmitCrossAccountAccessChange = async (data: FormValues) => {
    if (!account) {
      return
    }
    const accountConfig = sanitizeAccountConfig({ ...account.config, crossAccountAccess: data.crossAccountAccess }, validUserIds)
    const result = await updateAccount({
      accountId,
      payload: {
        config: accountConfig,
      },
    })
    if ('data' in result && result.data) {
      reset(getFormValuesFromAccount(result.data.data))
    } else {
      reset(getFormValuesFromAccount(account))
    }
  }

  const onSubmitCrossClientQueryAiPhaseAllowedChange = async (data: FormValues) => {
    if (!account) {
      return
    }
    const accountConfig = sanitizeAccountConfig({ ...account.config, crossClientQueryAiPhaseAllowed: data.crossClientQueryAiPhaseAllowed }, validUserIds)
    const result = await updateAccount({
      accountId,
      payload: {
        config: accountConfig,
      },
    })
    if ('data' in result && result.data) {
      reset(getFormValuesFromAccount(result.data.data))
    } else {
      reset(getFormValuesFromAccount(account))
    }
  }

  const onSubmitInviteLinkAutoRenewalChange = async (data: FormValues) => {
    if (!account) {
      return
    }
    const accountConfig = sanitizeAccountConfig({ ...account.config, inviteLinkAutoRenewal: data.inviteLinkAutoRenewalEnabled }, validUserIds)
    const result = await updateAccount({
      accountId,
      payload: {
        config: accountConfig,
      },
    })
    if ('data' in result && result.data) {
      reset(getFormValuesFromAccount(result.data.data))
    } else {
      reset(getFormValuesFromAccount(account))
    }
  }

  return (
    <>
      <Container fluid className='p-0'>
        <Row className='zocks-mb-2'>
          <Col>
            <h4 className='zocks-text-gray-900'>Account name</h4>
          </Col>
        </Row>
        {accountNameIsLockedAtEnterpriseLevel && <EnterpriseManagedBanner className='zocks-mb-2' message='Account name is locked at the enterprise level.' />}
        <Row className='mb-1'>
          <div className='paragraph-medium'>Account name</div>
        </Row>
        <Row>
          <Col className='pe-2 form-disabled-config'>
            <Controller
              render={({ field, fieldState }) => {
                return (
                  <>
                    <ScopedBlock
                      scope={{
                        scope: 'account:update',
                        superScope: ['account:admin'],
                      }}
                      missingScopeAction={MissingScopeAction.DISABLE}
                    >
                      <Form.Control
                        id={field.name}
                        type='text'
                        {...field}
                        placeholder='Account name'
                        isInvalid={!!fieldState.error}
                        disabled={accountNameIsLockedAtEnterpriseLevel || accountLoading || accountUpdateLoading}
                        maxLength={128}
                      />
                      {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                    </ScopedBlock>
                  </>
                )
              }}
              name='accountName'
              rules={{
                required: 'Account name is required.',
              }}
              control={control}
            />
          </Col>
          <Col xs='auto' className='ps-2'>
            <div className='d-flex gap-2'>
              <ScopedBlock
                scope={{
                  scope: 'account:update',
                  superScope: ['account:admin'],
                }}
                missingScopeAction={MissingScopeAction.DISABLE}
              >
                <Button
                  variant='secondary'
                  disabled={accountNameIsLockedAtEnterpriseLevel || accountLoading || accountUpdateLoading || !isAccountNameDirty}
                  onClick={onCancelAccountNameChange}
                >
                  Cancel
                </Button>
                <Button
                  variant='primary'
                  disabled={accountNameIsLockedAtEnterpriseLevel || accountLoading || accountUpdateLoading || !isAccountNameDirty}
                  onClick={handleSubmit(onSubmitAccountNameChange)}
                >
                  Save
                </Button>
              </ScopedBlock>
            </div>
          </Col>
        </Row>
        <Row className='zocks-mt-4.5'>
          <BusinessAddressSettings />
        </Row>
        <Row className='zocks-mt-4.5'>
          <AccountResidencySettings residency={account?.residency} />
        </Row>
        <Row className='zocks-mt-4.5'>
          <AccountRegionalSettings />
        </Row>
        <Row className='zocks-mt-4.5'>
          <AccountTimezoneSettings />
        </Row>
        <Row className='zocks-mt-4.5'>
          <Col>
            <h4 className='zocks-text-gray-900'>Allow cross-account access for external users</h4>
          </Col>
        </Row>
        <Container className='my-4'>
          <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
            <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>
                  You can grant cross-account access to members of other Zocks accounts, allowing them to join your team.
                </span>
              </div>
              {!crossAccountLoginQuota && (
                <ScopedBlock
                  scope={{
                    scope: 'account:update',
                    superScope: ['account:admin'],
                  }}
                  quota={{
                    quota: 'auth.crossAccountLoginEnabled.value',
                  }}
                  missingScopeAction={MissingScopeAction.UPGRADE}
                >
                  <Button variant={'primary'}>Upgrade</Button>
                </ScopedBlock>
              )}
            </Col>
          </Row>
        </Container>
        {crossAccountAccessIsManagedAtTheEnterpriseLevel ? (
          <Container className='zocks-mt-2.0 zocks-mb-2'>
            <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
              <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
                <div className='d-flex align-self-center gap-3 align-items-center'>
                  <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                  <span className='zocks-text-gray-900'>Cross-account access is managed at the enterprise level.</span>
                </div>
              </Col>
            </Row>
          </Container>
        ) : null}
        <Row>
          <Col>
            <div className='paragraph-medium'>Enable cross-account access</div>
          </Col>
          <Col>
            <Controller
              name='crossAccountAccess'
              control={control}
              render={({ field }) => {
                return (
                  <ScopedBlock
                    scope={{
                      scope: 'account:update',
                      superScope: ['account:admin'],
                    }}
                    quota={{
                      quota: 'auth.crossAccountLoginEnabled.value',
                    }}
                    missingScopeAction={MissingScopeAction.DISABLE}
                  >
                    <Form.Switch
                      disabled={crossAccountAccessIsManagedAtTheEnterpriseLevel || accountLoading || accountUpdateLoading}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e)
                        handleSubmit(onSubmitCrossAccountAccessChange)()
                      }}
                    />
                  </ScopedBlock>
                )
              }}
            />
          </Col>
        </Row>
        <Row className='zocks-mt-2.0'>
          <Col>
            <h4 className='zocks-text-gray-900'>Auto-renew expired invitations</h4>
          </Col>
        </Row>
        <Container className='my-4'>
          {inviteLinkAutoRenewalIsManagedAtEnterpriseLevel ? (
            <EnterpriseManagedBanner className='zocks-mb-2' message='Invite link auto-renewal is managed at the enterprise level.' />
          ) : null}
          <Row className={`${inviteLinkAutoRenewalQuota ? 'p-4' : 'p-3'} zocks-bg-color-primary-light gap-3 justify-content-center rounded`}>
            <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
              <div className='d-flex align-self-center gap-3 align-items-center'>
                <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                <span className='zocks-text-gray-900'>Automatically renew expired invitation links when users attempt to access them.</span>
              </div>
              {!inviteLinkAutoRenewalQuota && (
                <ScopedBlock
                  scope={{
                    scope: 'account:update',
                    superScope: ['account:admin'],
                  }}
                  quota={{
                    quota: 'auth.inviteLinkAutoRenewal.value',
                  }}
                  missingScopeAction={MissingScopeAction.UPGRADE}
                >
                  <Button variant={'primary'}>Upgrade</Button>
                </ScopedBlock>
              )}
            </Col>
          </Row>
          <Row className='gap-3 p-0 align-items-center'>
            <Col className='p-0'>
              <div className='paragraph-medium'>Enable link auto-renewal</div>
            </Col>
            <Col className='p-4'>
              <Controller
                name='inviteLinkAutoRenewalEnabled'
                control={control}
                render={({ field }) => {
                  return (
                    <ScopedBlock
                      scope={{
                        scope: 'account:update',
                        superScope: ['account:admin'],
                      }}
                      quota={{
                        quota: 'auth.inviteLinkAutoRenewal.value',
                      }}
                      missingScopeAction={MissingScopeAction.DISABLE}
                    >
                      <Form.Switch
                        disabled={inviteLinkAutoRenewalIsManagedAtEnterpriseLevel || accountLoading || accountUpdateLoading}
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e)
                          handleSubmit(onSubmitInviteLinkAutoRenewalChange)()
                        }}
                      />
                    </ScopedBlock>
                  )
                }}
              />
            </Col>
          </Row>
        </Container>
        {cqQuota ? (
          <>
            <Row className='zocks-mt-2.0'>
              <Col>
                <h4 className='zocks-text-gray-900 mb-2'>Client Queries access</h4>
              </Col>
            </Row>
            <Container fluid className='mb-4 px-0'>
              <p className='paragraph-medium zocks-text-gray-900 mb-4'>
                AI Deep Analysis takes a closer look at possible matches. It uses credits per client, only when someone runs it.
              </p>
              {crossClientQueryAiPhaseAllowedIsManagedAtTheEnterpriseLevel ? (
                <div className='d-flex align-items-center zocks-gap-0.75 p-3 mb-4 border rounded-3 zocks-bg-color-gray-100 zocks-border-color-gray-300'>
                  <i className='bi bi-building zocks-text-gray-900' />
                  <span className='paragraph-small zocks-text-gray-900'>AI Deep Analysis is managed at the enterprise level. It can't be changed here.</span>
                </div>
              ) : null}
              <Row className='gap-3 zocks-py-0.75 mx-0 align-items-center'>
                <Col className='p-0'>
                  <div className='paragraph-medium'>Enable AI Deep Analysis</div>
                </Col>
                <Col className='p-0'>
                  <Controller
                    name='crossClientQueryAiPhaseAllowed'
                    control={control}
                    render={({ field }) => {
                      return (
                        <ScopedBlock
                          scope={{
                            scope: 'account:update',
                            superScope: ['account:admin'],
                          }}
                          missingScopeAction={MissingScopeAction.DISABLE}
                        >
                          <Form.Switch
                            disabled={crossClientQueryAiPhaseAllowedIsManagedAtTheEnterpriseLevel || accountLoading || accountUpdateLoading}
                            checked={field.value}
                            onChange={(e) => {
                              field.onChange(e)
                              handleSubmit(onSubmitCrossClientQueryAiPhaseAllowedChange)()
                            }}
                          />
                        </ScopedBlock>
                      )
                    }}
                  />
                </Col>
              </Row>
              {crossClientQueryAiPhaseAllowed && account ? (
                <Row className='mt-3'>
                  <Col>
                    {crossClientQueryAiPhaseAllowedRolesIsManagedAtTheEnterpriseLevel ? (
                      <EnterpriseManagedBanner message='AI Deep Analysis access is configured by Role at the enterprise level' />
                    ) : (
                      <ClientQueryUserAccessTable account={account} />
                    )}
                  </Col>
                </Row>
              ) : null}
            </Container>
          </>
        ) : null}
      </Container>
    </>
  )
}
